import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Input,
  Button,
  Select,
  FormControl,
  FormLabel,
  useToast,
  Container,
  Heading,
  Divider,
  Badge,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';
import { useAuth } from '../contexts/AuthContext';
import { sendEmailVerificationToUser, verifyEmail } from '../services/authService';
import { useNavigate } from 'react-router-dom';

const EditProfile: React.FC = () => {
  const { user, userProfile, updateProfile } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const [formData, setFormData] = useState({
    username: '',
    name: '',
    email: '',
    phoneNumber: '',
    language: 'en',
    location: '',
    age: '',
    gender: '',
    backupEmail: '',
    bio: '',
  });

  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isSendingVerification, setIsSendingVerification] = useState(false);

  useEffect(() => {
    if (userProfile) {
      setFormData({
        username: userProfile.username || '',
        name: userProfile.name || '',
        email: userProfile.email || '',
        phoneNumber: userProfile.phoneNumber || '',
        language: userProfile.language || 'en',
        location: userProfile.location || '',
        age: userProfile.age?.toString() || '',
        gender: userProfile.gender || '',
        backupEmail: userProfile.backupEmail || '',
        bio: userProfile.bio || '',
      });
      // Use Firebase user's emailVerified status instead of profile data
      setIsEmailVerified(user?.emailVerified || false);
      setIsPhoneVerified(userProfile.isPhoneVerified || false);
    }
  }, [userProfile, user]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!user) return;

    try {
      setIsUpdating(true);
      await updateProfile({
        username: formData.username,
        name: formData.name,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        language: formData.language,
        location: formData.location,
        age: formData.age ? parseInt(formData.age) : undefined,
        gender: formData.gender,
        backupEmail: formData.backupEmail,
        bio: formData.bio,
      });

      toast({
        title: 'Profile updated successfully!',
        status: 'success',
      });
    } catch (error: any) {
      toast({
        title: 'Failed to update profile',
        description: error.message,
        status: 'error',
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSendEmailVerification = async () => {
    try {
      setIsSendingVerification(true);
      await sendEmailVerificationToUser();
      toast({
        title: 'Verification email sent!',
        description: 'Please check your email and click the verification link.',
        status: 'success',
      });
    } catch (error: any) {
      toast({
        title: 'Failed to send verification email',
        description: error.message,
        status: 'error',
      });
    } finally {
      setIsSendingVerification(false);
    }
  };

  const handleCheckEmailVerification = async () => {
    try {
      const verified = await verifyEmail();
      if (verified) {
        setIsEmailVerified(true);
        toast({
          title: 'Email verified successfully!',
          status: 'success',
        });
      } else {
        toast({
          title: 'Email not yet verified',
          description: 'Please check your email and click the verification link.',
          status: 'info',
        });
      }
    } catch (error: any) {
      toast({
        title: 'Failed to check email verification',
        description: error.message,
        status: 'error',
      });
    }
  };

  if (!user) {
    return (
      <Container maxW="container.md" py={8}>
        <Text>Please log in to edit your profile.</Text>
      </Container>
    );
  }

  return (
    <Container maxW="container.md" py={8}>
      <VStack gap={8} align="stretch">
        <VStack gap={2} textAlign="center">
          <Heading size="lg" color="white">Edit Profile</Heading>
          <Text color="gray.400">Update your account information</Text>
        </VStack>

        {/* Verification Status */}
        <VStack gap={4} align="stretch">
          <Text fontWeight="bold" color="white">Verification Status</Text>
          
          <HStack gap={4}>
            <Badge colorScheme={isEmailVerified ? 'green' : 'red'} fontSize="sm">
              Email: {isEmailVerified ? 'Verified' : 'Not Verified'}
            </Badge>
            <Badge colorScheme={isPhoneVerified ? 'green' : 'red'} fontSize="sm">
              Phone: {isPhoneVerified ? 'Verified' : 'Not Verified'}
            </Badge>
          </HStack>

          {!isEmailVerified && (
            <Alert status="warning">
              <AlertIcon />
              <Box>
                <AlertTitle>Email not verified!</AlertTitle>
                <AlertDescription>
                  <VStack align="start" gap={2}>
                    <Text fontSize="sm">
                      {user?.providerData?.some(provider => provider.providerId === 'google.com') 
                        ? 'Google accounts are automatically verified. If you see this message, please refresh the page.'
                        : 'Please verify your email address to access all features.'
                      }
                    </Text>
                    <HStack gap={2}>
                      <Button size="sm" onClick={handleSendEmailVerification} isLoading={isSendingVerification}>
                        Send Verification Email
                      </Button>
                      <Button size="sm" variant="outline" onClick={handleCheckEmailVerification}>
                        Check Verification
                      </Button>
                    </HStack>
                  </VStack>
                </AlertDescription>
              </Box>
            </Alert>
          )}

          {isEmailVerified && (
            <Alert status="success">
              <AlertIcon />
              <AlertDescription>
                <Text fontSize="sm">
                  {user?.providerData?.some(provider => provider.providerId === 'google.com') 
                    ? 'Email verified via Google sign-in'
                    : 'Email address is verified'
                  }
                </Text>
              </AlertDescription>
            </Alert>
          )}
        </VStack>

        <Divider />

        {/* Profile Form */}
        <VStack gap={6} align="stretch">
          <VStack gap={4} align="stretch">
            <FormControl>
              <FormLabel color="gray.300">Username</FormLabel>
              <Input
                placeholder="Username"
                bg="gray.700"
                border="none"
                color="white"
                _placeholder={{ color: 'gray.400' }}
                _focus={{ bg: 'gray.600' }}
                value={formData.username}
                onChange={(e) => handleInputChange('username', e.target.value)}
              />
            </FormControl>

            <FormControl>
              <FormLabel color="gray.300">Name</FormLabel>
              <Input
                placeholder="Full Name"
                bg="gray.700"
                border="none"
                color="white"
                _placeholder={{ color: 'gray.400' }}
                _focus={{ bg: 'gray.600' }}
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
              />
            </FormControl>

            <FormControl>
              <FormLabel color="gray.300">Email</FormLabel>
              <Input
                placeholder="Email"
                type="email"
                bg="gray.700"
                border="none"
                color="white"
                _placeholder={{ color: 'gray.400' }}
                _focus={{ bg: 'gray.600' }}
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
              />
            </FormControl>

            <FormControl>
              <FormLabel color="gray.300">Phone Number</FormLabel>
              <Input
                placeholder="Phone Number"
                type="tel"
                bg="gray.700"
                border="none"
                color="white"
                _placeholder={{ color: 'gray.400' }}
                _focus={{ bg: 'gray.600' }}
                value={formData.phoneNumber}
                onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
              />
            </FormControl>

            <FormControl>
              <FormLabel color="gray.300">Language</FormLabel>
              <Select
                bg="gray.700"
                border="none"
                color="white"
                _focus={{ bg: 'gray.600' }}
                value={formData.language}
                onChange={(e) => handleInputChange('language', e.target.value)}
              >
                <option value="en">English</option>
                <option value="es">Spanish</option>
                <option value="fr">French</option>
                <option value="de">German</option>
                <option value="it">Italian</option>
                <option value="pt">Portuguese</option>
                <option value="ru">Russian</option>
                <option value="ja">Japanese</option>
                <option value="ko">Korean</option>
                <option value="zh">Chinese</option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel color="gray.300">Location</FormLabel>
              <Input
                placeholder="Location"
                bg="gray.700"
                border="none"
                color="white"
                _placeholder={{ color: 'gray.400' }}
                _focus={{ bg: 'gray.600' }}
                value={formData.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
              />
            </FormControl>

            <FormControl>
              <FormLabel color="gray.300">Age</FormLabel>
              <Input
                placeholder="Age"
                type="number"
                bg="gray.700"
                border="none"
                color="white"
                _placeholder={{ color: 'gray.400' }}
                _focus={{ bg: 'gray.600' }}
                value={formData.age}
                onChange={(e) => handleInputChange('age', e.target.value)}
              />
            </FormControl>

            <FormControl>
              <FormLabel color="gray.300">Gender</FormLabel>
              <Select
                bg="gray.700"
                border="none"
                color="white"
                _focus={{ bg: 'gray.600' }}
                value={formData.gender}
                onChange={(e) => handleInputChange('gender', e.target.value)}
              >
                <option value="">Select Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="non-binary">Non-binary</option>
                <option value="other">Other</option>
                <option value="prefer-not-to-say">Prefer not to say</option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel color="gray.300">Backup Email</FormLabel>
              <Input
                placeholder="Backup Email"
                type="email"
                bg="gray.700"
                border="none"
                color="white"
                _placeholder={{ color: 'gray.400' }}
                _focus={{ bg: 'gray.600' }}
                value={formData.backupEmail}
                onChange={(e) => handleInputChange('backupEmail', e.target.value)}
              />
            </FormControl>

            <FormControl>
              <FormLabel color="gray.300">Bio</FormLabel>
              <Input
                placeholder="Tell us about yourself"
                bg="gray.700"
                border="none"
                color="white"
                _placeholder={{ color: 'gray.400' }}
                _focus={{ bg: 'gray.600' }}
                value={formData.bio}
                onChange={(e) => handleInputChange('bio', e.target.value)}
              />
            </FormControl>
          </VStack>

          <HStack gap={4} justify="center">
            <Button
              colorScheme="purple"
              size="lg"
              onClick={handleSave}
              isLoading={isUpdating}
              loadingText="Saving"
            >
              Save Changes
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => navigate('/profile')}
            >
              Cancel
            </Button>
          </HStack>
        </VStack>
      </VStack>
    </Container>
  );
};

export default EditProfile;
